/**
 * DeepAnalysisSection — Deep Analysis Phase 5 (UI)
 *
 * Renders the 6-specialist Deep Analysis on the Stock Detail page,
 * directly under the Target Price card. Handles all 5 states:
 *
 *   State 1 — No analysis yet (Premium)
 *   State 2 — Analysis available (Premium)        ← richest UI
 *   State 3 — Running with progress
 *   State 4 — Outdated (>30 days)
 *   State 5 — Free / Basic user (locked)
 *
 * Backend endpoints (Phase 3):
 *   GET  /api/stocks/{symbol}/deep-analysis        (full payload)
 *   GET  /api/stocks/{symbol}/deep-analysis/status (fast status only)
 *   POST /api/stocks/{symbol}/deep-analysis        (generate / re-use)
 *   POST /api/stocks/{symbol}/deep-analysis/personal-context
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  RefreshCcw,
  Lock,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Shield,
  DollarSign,
  Globe2,
  LineChart,
  Award,
  Clock,
  Info,
  CheckCircle2,
  User,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SignalBadge from "@/components/common/SignalBadge";
import { AiAnalysisWarning } from "@/components/legal/DataNotes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const SIGNAL_TONE = {
  "STRONG BUY": "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  BUY: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
  WATCH: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground border-border",
  AVOID: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/25",
};

const VERDICT_TONE = {
  "STRONG BUY": "text-[hsl(var(--success))]",
  BUY: "text-[hsl(var(--success))]",
  "FAIR VALUE": "text-muted-foreground",
  OVERVALUED: "text-[hsl(var(--destructive))]",

  HIGH: "text-[hsl(var(--success))]",
  GOOD: "text-[hsl(var(--success))]",
  AVERAGE: "text-muted-foreground",
  POOR: "text-[hsl(var(--destructive))]",

  EXCELLENT: "text-[hsl(var(--success))]",
  SAFE: "text-[hsl(var(--success))]",
  ADEQUATE: "text-muted-foreground",
  "AT RISK": "text-amber-600 dark:text-amber-400",
  UNSAFE: "text-[hsl(var(--destructive))]",

  LOW: "text-[hsl(var(--success))]",
  MODERATE: "text-amber-600 dark:text-amber-400",
  "VERY HIGH": "text-[hsl(var(--destructive))]",

  TAILWIND: "text-[hsl(var(--success))]",
  NEUTRAL: "text-muted-foreground",
  HEADWIND: "text-amber-600 dark:text-amber-400",
  "STRONG HEADWIND": "text-[hsl(var(--destructive))]",

  SUPPORTIVE: "text-[hsl(var(--success))]",
  CHALLENGING: "text-amber-600 dark:text-amber-400",
};

const fmtAge = (days) => {
  if (days == null) return "—";
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return `${Math.floor(days / 30)} mo ago`;
};

const fmtScore = (s) => (s == null ? "—" : Number(s).toFixed(0));
const fmtPct = (v, dp = 2) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(dp)}%`;
const fmtLKR0 = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `Rs. ${Number(v).toLocaleString("en-LK", { maximumFractionDigits: 0 })}`;

// ---------------------------------------------------------------------------
// Small UI atoms
// ---------------------------------------------------------------------------
const VerdictPill = ({ label, tone, testId }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border bg-card ${
      tone || "text-muted-foreground border-border"
    }`}
    data-testid={testId}
  >
    {label}
  </span>
);

const ScoreChip = ({ score, range }) => (
  <span className="text-[11px] tabular-nums font-medium text-muted-foreground">
    {fmtScore(score)}
    <span className="text-muted-foreground/60">/{range}</span>
  </span>
);

// ---------------------------------------------------------------------------
// Specialist accordion item
// ---------------------------------------------------------------------------
const SPECIALIST_META = {
  value_analysis: {
    title: "Value Specialist",
    icon: TrendingUp,
    range: 25,
    testId: "specialist-value",
  },
  quality_analysis: {
    title: "Quality Specialist",
    icon: Award,
    range: 25,
    testId: "specialist-quality",
  },
  dividend_analysis: {
    title: "Dividend Specialist",
    icon: DollarSign,
    range: 20,
    testId: "specialist-dividend",
  },
  risk_analysis: {
    title: "Risk Specialist",
    icon: Shield,
    range: 15,
    testId: "specialist-risk",
  },
  geo_analysis: {
    title: "Geopolitical Specialist",
    icon: Globe2,
    range: "+5/-5",
    testId: "specialist-geo",
  },
  macro_analysis: {
    title: "Sri Lanka Macro Specialist",
    icon: LineChart,
    range: "+5/-5",
    testId: "specialist-macro",
  },
};

const SpecialistRow = ({ k, label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{k}</span>
      <span className="text-foreground">{label || value}</span>
    </div>
  );
};

const SpecialistList = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
};

const SpecialistCard = ({ kind, data }) => {
  const meta = SPECIALIST_META[kind];
  if (!meta || !data) return null;
  const Icon = meta.icon;
  const verdict = data.verdict || "—";
  return (
    <AccordionItem
      value={kind}
      className="border rounded-lg bg-card data-[state=open]:shadow-sm"
      data-testid={meta.testId}
    >
      <AccordionTrigger className="px-3.5 py-3 hover:no-underline">
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <Icon className="h-4 w-4 text-primary shrink-0" />
          <span className="font-display text-sm font-medium text-foreground truncate">
            {meta.title}
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <VerdictPill
              label={verdict}
              tone={VERDICT_TONE[verdict] || ""}
              testId={`${meta.testId}-verdict`}
            />
            <ScoreChip score={data.score} range={meta.range} />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3.5 pb-3.5 space-y-3">
        {data.summary && (
          <p className="text-sm leading-relaxed text-foreground/90" data-testid={`${meta.testId}-summary`}>
            {data.summary}
          </p>
        )}

        {/* Value-specific extras */}
        {kind === "value_analysis" && (
          <div className="space-y-1.5">
            <SpecialistRow k="P/E" label={data.pe_assessment} value={data.pe_assessment} />
            <SpecialistRow k="Graham" label={data.graham_assessment} value={data.graham_assessment} />
          </div>
        )}

        {/* Quality-specific extras */}
        {kind === "quality_analysis" && (
          <div className="space-y-1.5">
            <SpecialistRow k="ROE Trend" label={data.roe_trend} value={data.roe_trend} />
            <SpecialistRow
              k="Earnings Quality"
              label={data.earnings_quality}
              value={data.earnings_quality}
            />
          </div>
        )}

        {/* Dividend-specific extras */}
        {kind === "dividend_analysis" && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border bg-muted/30 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Gross yield
              </div>
              <div className="font-display text-base tabular-nums">
                {fmtPct(data.gross_yield)}
              </div>
            </div>
            <div className="rounded-md border bg-muted/30 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Net after 10% WHT
              </div>
              <div className="font-display text-base tabular-nums text-[hsl(var(--success))]">
                {fmtPct(data.net_yield_after_wht)}
              </div>
            </div>
            <div className="col-span-2 rounded-md border border-dashed bg-card p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Income on Rs.100,000 invested
              </div>
              <div className="font-display text-base tabular-nums">
                {fmtLKR0(data.income_per_100k_invested)} / year
              </div>
            </div>
            <SpecialistRow
              k="Sustainability"
              label={data.sustainability}
              value={data.sustainability}
            />
            <SpecialistRow
              k="Growth"
              label={data.growth_outlook}
              value={data.growth_outlook}
            />
          </div>
        )}

        {/* Risk-specific extras */}
        {kind === "risk_analysis" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 text-[11px]">
              {data.debt_risk && (
                <Badge variant="outline" className="font-normal">
                  Debt risk: <span className="ml-1 font-semibold">{data.debt_risk}</span>
                </Badge>
              )}
              {data.liquidity_risk && (
                <Badge variant="outline" className="font-normal">
                  Liquidity risk:{" "}
                  <span className="ml-1 font-semibold">{data.liquidity_risk}</span>
                </Badge>
              )}
            </div>
            {data.key_risks?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  Key risks
                </div>
                <SpecialistList items={data.key_risks} />
              </div>
            )}
            {data.mitigants?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  Mitigants
                </div>
                <SpecialistList items={data.mitigants} />
              </div>
            )}
          </div>
        )}

        {/* Geo-specific extras */}
        {kind === "geo_analysis" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 text-[11px]">
              {data.oil_impact && (
                <Badge variant="outline" className="font-normal">
                  Oil: <span className="ml-1 font-semibold">{data.oil_impact}</span>
                </Badge>
              )}
              {data.lkr_impact && (
                <Badge variant="outline" className="font-normal">
                  LKR: <span className="ml-1 font-semibold">{data.lkr_impact}</span>
                </Badge>
              )}
              {data.foreign_flow_risk && (
                <Badge variant="outline" className="font-normal">
                  Foreign flow:{" "}
                  <span className="ml-1 font-semibold">{data.foreign_flow_risk}</span>
                </Badge>
              )}
            </div>
            {data.key_risks?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  Risks
                </div>
                <SpecialistList items={data.key_risks} />
              </div>
            )}
            {data.key_opportunities?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  Opportunities
                </div>
                <SpecialistList items={data.key_opportunities} />
              </div>
            )}
          </div>
        )}

        {/* Macro-specific extras */}
        {kind === "macro_analysis" && (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-md border bg-muted/30 p-2">
                <div className="uppercase tracking-wide text-muted-foreground">AWPR</div>
                <div className="tabular-nums font-medium">
                  {data.current_awpr != null ? `${data.current_awpr}%` : "—"}
                </div>
              </div>
              <div className="rounded-md border bg-muted/30 p-2">
                <div className="uppercase tracking-wide text-muted-foreground">Inflation</div>
                <div className="tabular-nums font-medium">
                  {data.current_inflation != null ? `${data.current_inflation}%` : "—"}
                </div>
              </div>
              <div className="rounded-md border bg-muted/30 p-2">
                <div className="uppercase tracking-wide text-muted-foreground">FX Reserves</div>
                <div className="tabular-nums font-medium">
                  {data.fx_reserves != null ? `${data.fx_reserves}` : "—"}
                </div>
              </div>
            </div>
            <SpecialistRow
              k="Interest rates"
              label={data.interest_rate_impact}
              value={data.interest_rate_impact}
            />
            <SpecialistRow
              k="Inflation"
              label={data.inflation_impact}
              value={data.inflation_impact}
            />
            <SpecialistRow k="IMF" label={data.imf_impact} value={data.imf_impact} />
            <SpecialistRow
              k="CBSL policy"
              label={data.cbsl_policy_impact}
              value={data.cbsl_policy_impact}
            />
          </div>
        )}

        {data.key_points?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              Key points
            </div>
            <SpecialistList items={data.key_points} />
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

// Helper: split verdict into "first 3 sentences" preview + remainder
const splitVerdict = (text) => {
  if (!text || typeof text !== "string") return { head: text || "", tail: "" };
  const t = text.trim();
  // Split on sentence-ending punctuation followed by whitespace; keep the punctuation.
  const re = /([.!?])\s+/g;
  let m;
  let count = 0;
  let cut = -1;
  while ((m = re.exec(t)) !== null) {
    count += 1;
    if (count === 3) {
      cut = m.index + 1;
      break;
    }
  }
  if (cut === -1 || cut >= t.length - 5) return { head: t, tail: "" };
  return { head: t.slice(0, cut).trim(), tail: t.slice(cut).trim() };
};

// ---------------------------------------------------------------------------
// Coordinator card (top-of-section synthesis)
// ---------------------------------------------------------------------------
const CoordinatorCard = ({ coord }) => {
  const [expanded, setExpanded] = useState(false);
  if (!coord) return null;
  const signal = (coord.composite_signal || "HOLD").toUpperCase();
  const { head, tail } = splitVerdict(coord.investment_verdict);
  return (
    <div
      className="rounded-lg border-2 bg-gradient-to-br from-primary/5 to-card p-4 space-y-3"
      data-testid="deep-analysis-coordinator"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Investment verdict
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Use the shared SignalBadge so the colour/icon/shape is
              identical to the Stocks screener & Track Record badges.
              Wrapped in a span so the legacy deep-analysis-signal
              testId is still selectable. */}
          <span data-testid="deep-analysis-signal" className="inline-flex">
            <SignalBadge signal={signal} size="lg" />
          </span>          {coord.conviction_level && (
            <Badge variant="outline" className="text-[11px]" data-testid="deep-analysis-conviction">
              {coord.conviction_level} conviction
            </Badge>
          )}
        </div>
      </div>

      {coord.investment_verdict && (
        <div data-testid="deep-analysis-verdict">
          <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
            {expanded || !tail ? coord.investment_verdict : head}
          </p>
          {tail && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1.5 text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
              data-testid="deep-analysis-verdict-toggle"
            >
              {expanded ? "Show less ▲" : "Read more ▼"}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
        {coord.entry_strategy && (
          <div className="rounded-md border bg-card p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Entry strategy
            </div>
            <div className="font-medium text-sm capitalize">{coord.entry_strategy}</div>
            {coord.entry_detail && (
              <div className="text-xs text-muted-foreground mt-1">{coord.entry_detail}</div>
            )}
          </div>
        )}
        {coord.position_size_guidance && (
          <div className="rounded-md border bg-card p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Position size
            </div>
            <div className="text-xs text-foreground/90 leading-snug">
              {coord.position_size_guidance}
            </div>
          </div>
        )}
        {coord.time_horizon && (
          <div className="rounded-md border bg-card p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Time horizon
            </div>
            <div className="font-medium text-sm">{coord.time_horizon}</div>
          </div>
        )}
      </div>

      {(coord.key_agreements?.length > 0 || coord.key_contradictions?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          {coord.key_agreements?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--success))] mb-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Specialists agree
              </div>
              <SpecialistList items={coord.key_agreements} />
            </div>
          )}
          {coord.key_contradictions?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Specialists disagree
              </div>
              <SpecialistList items={coord.key_contradictions} />
            </div>
          )}
        </div>
      )}

      {(coord.catalysts_to_watch?.length > 0 || coord.exit_criteria?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          {coord.catalysts_to_watch?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Catalysts to watch
              </div>
              <SpecialistList items={coord.catalysts_to_watch} />
            </div>
          )}
          {coord.exit_criteria?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Exit criteria
              </div>
              <SpecialistList items={coord.exit_criteria} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Personal context block
// ---------------------------------------------------------------------------
const PersonalContextCard = ({ personal, loading, onGenerate }) => {
  if (!personal && !loading) {
    return (
      <div
        className="rounded-lg border border-dashed bg-muted/20 p-3.5 text-sm flex items-start justify-between gap-3"
        data-testid="personal-context-empty"
      >
        <div className="flex items-start gap-2 text-muted-foreground">
          <User className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Add your portfolio context — see how this stock fits your holdings and sector caps
            (free, takes ~5 seconds).
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={onGenerate} data-testid="personal-context-btn">
          Add my context
        </Button>
      </div>
    );
  }
  if (loading && !personal) {
    return (
      <div className="rounded-lg border bg-muted/30 p-3.5 text-sm flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Adding your portfolio context…
      </div>
    );
  }
  const ctx = personal.portfolio_context || {};
  return (
    <div
      className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4 space-y-3"
      data-testid="personal-context-card"
    >
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-primary" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Your personal context
        </span>
      </div>
      {personal.personal_verdict && (
        <p className="text-sm leading-relaxed text-foreground" data-testid="personal-verdict">
          {personal.personal_verdict}
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="rounded-md border bg-card p-2">
          <div className="uppercase tracking-wide text-muted-foreground text-[10px]">
            Shares held
          </div>
          <div className="tabular-nums font-medium">{ctx.shares_held ?? 0}</div>
        </div>
        <div className="rounded-md border bg-card p-2">
          <div className="uppercase tracking-wide text-muted-foreground text-[10px]">Avg cost</div>
          <div className="tabular-nums font-medium">{fmtLKR0(ctx.avg_cost)}</div>
        </div>
        <div className="rounded-md border bg-card p-2">
          <div className="uppercase tracking-wide text-muted-foreground text-[10px]">
            Current value
          </div>
          <div className="tabular-nums font-medium">{fmtLKR0(ctx.current_value)}</div>
        </div>
        <div className="rounded-md border bg-card p-2">
          <div className="uppercase tracking-wide text-muted-foreground text-[10px]">
            Unrealised P/L
          </div>
          <div
            className={`tabular-nums font-medium ${
              (ctx.unrealised_gain_pct ?? 0) >= 0
                ? "text-[hsl(var(--success))]"
                : "text-[hsl(var(--destructive))]"
            }`}
          >
            {fmtPct(ctx.unrealised_gain_pct)}
          </div>
        </div>
      </div>
      {ctx.personal_recommendation && (
        <div className="rounded-md border border-dashed bg-card p-2.5 text-sm leading-relaxed">
          {ctx.personal_recommendation}
        </div>
      )}
      {ctx.shares_can_add != null && (
        <div className="text-xs text-muted-foreground">
          Within sector caps you may add up to{" "}
          <span className="tabular-nums font-medium text-foreground">
            {Number(ctx.shares_can_add).toLocaleString("en-LK")}
          </span>{" "}
          more shares.
          {ctx.at_sector_cap && (
            <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
              At sector cap
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// State 5 — Free / Basic locked
// ---------------------------------------------------------------------------
const LockedState = ({ symbol, status }) => (
  <Card
    className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-card"
    data-testid="deep-analysis-locked"
  >
    <CardHeader className="pb-3">
      <CardTitle className="font-display text-base flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        Deep Analysis
        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
          Premium
        </Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {status?.has_shared_analysis && (
        <div
          className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 p-2.5 text-sm flex items-center gap-2 text-[hsl(var(--success))]"
          data-testid="deep-analysis-locked-teaser"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>
            Community analysis available — last run{" "}
            <span className="font-semibold tabular-nums">
              {fmtAge(status.analysis_age_days)}
            </span>
          </span>
        </div>
      )}
      <p className="text-sm text-muted-foreground leading-relaxed">
        Deep Analysis is a 6-specialist research synthesis with live Sri Lanka macro and
        geopolitical context for {symbol} and every CSE stock.
      </p>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground pt-1">
        What's included
      </div>
      <ul className="text-sm text-foreground/90 space-y-1.5">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <span>View all community analyses (free during beta)</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <span>Add your portfolio context — sector caps, position sizing, yield-on-cost</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <span>Macro &amp; geopolitical context refreshed weekly from CBSL</span>
        </li>
        <li className="flex items-start gap-2 text-muted-foreground">
          <Clock className="h-4 w-4 mt-0.5 shrink-0" />
          <span>PDF export (coming soon)</span>
        </li>
      </ul>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// State 1 — No analysis yet (Premium)
// ---------------------------------------------------------------------------
const EmptyState = ({ symbol, status, onGenerate, isAdmin }) => {
  const left = status?.user_quota_remaining;
  const limit = status?.user_quota_limit;
  // Admin detection: trust both the auth-context flag AND the backend flag.
  // Admins NEVER see quota limits anywhere in the app.
  const adminMode = !!isAdmin || status?.is_admin === true || limit === 999 || limit === -1;
  const exhausted = !adminMode && (left ?? 0) <= 0 && (limit ?? 0) > 0;
  const resetsAt = status?.quota_resets_at ? new Date(status.quota_resets_at) : null;
  const daysToReset = resetsAt
    ? Math.max(0, Math.ceil((resetsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  if (exhausted) {
    return (
      <Card className="border-2 border-amber-500/40 bg-amber-500/5" data-testid="deep-analysis-quota-exhausted">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Deep Analysis
            <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 dark:text-amber-400">
              Quota reached
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-foreground leading-relaxed">
            You have used all{" "}
            <span className="tabular-nums font-semibold">{limit}</span> deep analyses this month.
          </p>
          <div className="rounded-md border bg-card p-3 text-sm">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              Resets in
            </div>
            <div className="font-display text-lg tabular-nums">
              {daysToReset != null ? `${daysToReset} day${daysToReset === 1 ? "" : "s"}` : "—"}
              {resetsAt && (
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  on {resetsAt.toLocaleDateString("en-LK", { month: "short", day: "numeric" })}
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Need more analyses sooner?{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact us
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 bg-card" data-testid="deep-analysis-empty">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          Deep Analysis
          <Badge variant="secondary" className="text-[10px]">6-specialist research</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          6-specialist analysis covering Value, Quality, Dividend, Risk, Geopolitical context
          and Sri Lanka Macro economics from CBSL — synthesised by Claude into a single
          coordinated investment verdict.
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> Shared with community
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Takes ~2 minutes
          </span>
          <span className="inline-flex items-center gap-1.5">
            <RefreshCcw className="h-3.5 w-3.5" /> Cached 30 days
          </span>
        </div>
        {adminMode ? (
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <div className="text-xs text-muted-foreground" data-testid="deep-analysis-quota-line">
              <span className="inline-flex items-center gap-1.5 text-[hsl(var(--success))] font-medium">
                <Shield className="h-3.5 w-3.5" />
                Admin · Unlimited access
              </span>
            </div>
            <Button onClick={onGenerate} data-testid="deep-analysis-generate-btn">
              <Sparkles className="h-4 w-4 mr-2" />
              Run Deep Analysis
            </Button>
          </div>
        ) : (
          // BETA MODE — regular users can VIEW any shared analysis but
          // only admins can RUN new ones (cost control). Show a neutral
          // "view-only" message instead of the upgrade prompt.
          <div
            className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2"
            data-testid="deep-analysis-view-only"
          >
            <Shield className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <span>
              No deep analysis has been published for {symbol} yet. New analyses are
              triggered by administrators during the beta — once published, you'll see
              the full 6-specialist verdict here.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// State 3 — Running with progress (driven by backend polling)
// ---------------------------------------------------------------------------
const STAGE_FALLBACK_LABELS = {
  initialising: "Loading stock fundamentals & macro/geo context…",
  value_quality_dividend: "Analysing value, quality and dividends…",
  risk_geo_macro: "Analysing risk, geopolitical context and Sri Lanka macro…",
  coordinator: "Coordinator synthesising all specialist findings…",
  complete: "Analysis complete!",
  failed: "Analysis failed",
};

const STAGE_ORDER = [
  "initialising",
  "value_quality_dividend",
  "risk_geo_macro",
  "coordinator",
  "complete",
];

const RunningState = ({ symbol, progress, startedAt }) => {
  // Estimated total ~150s; cap at 95% until the backend reports complete.
  const ESTIMATE_S = 150;

  // Client-side fallback timer — increments every second so the user sees
  // motion even if a poll tick is briefly delayed by the network. We
  // anchor against `startedAt` (server timestamp) when available so the
  // count stays accurate across navigations and tab switches.
  const [clientNow, setClientNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClientNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const serverElapsed = progress?.elapsed_seconds ?? 0;
  let clientElapsed = 0;
  if (startedAt) {
    const t = new Date(startedAt).getTime();
    if (Number.isFinite(t)) clientElapsed = Math.max(0, Math.floor((clientNow - t) / 1000));
  }
  // Prefer the CLIENT-SIDE counter when we have a known startedAt — it ticks
  // smoothly every second. The backend value is only refreshed on each /status
  // poll tick (every 5s). Use server value as fallback when startedAt is missing.
  const elapsed = startedAt ? clientElapsed : serverElapsed;

  const completedStages = progress?.completed_stages ?? 0;
  const totalStages = progress?.total_stages ?? 3;
  const currentStage = progress?.stage || "initialising";
  const currentLabel = progress?.stage_label || STAGE_FALLBACK_LABELS[currentStage] || "Working…";

  // Drive bar from BOTH stage progress and elapsed time so users always see motion.
  const stagePct = Math.round((completedStages / Math.max(totalStages, 1)) * 100);
  const timePct = Math.min(95, Math.round((elapsed / ESTIMATE_S) * 100));
  const pct = Math.min(95, Math.max(stagePct, timePct));

  // Build the visual stage list (initialising is a soft sub-stage; we display the 3 real ones).
  const visibleStages = [
    { key: "value_quality_dividend", label: "Value · Quality · Dividend specialists" },
    { key: "risk_geo_macro", label: "Risk · Geopolitical · Sri Lanka Macro specialists" },
    { key: "coordinator", label: "Coordinator synthesis" },
  ];

  const currentIdx = STAGE_ORDER.indexOf(currentStage);

  return (
    <Card className="border-2 border-primary/30 bg-card" data-testid="deep-analysis-running">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          Generating Deep Analysis · {symbol}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={pct} className="h-2" data-testid="deep-analysis-progress" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="deep-analysis-stage">{currentLabel}</span>
          <span className="tabular-nums" data-testid="deep-analysis-elapsed">
            Running for {elapsed}s
          </span>
        </div>
        <ul className="space-y-1.5 pt-1">
          {visibleStages.map((s) => {
            const sIdx = STAGE_ORDER.indexOf(s.key);
            const isDone = currentIdx > sIdx || currentStage === "complete";
            const isActive = currentStage === s.key;
            return (
              <li
                key={s.key}
                className={`flex items-center gap-2 text-xs ${
                  isDone
                    ? "text-foreground/70 line-through decoration-muted"
                    : isActive
                      ? "text-foreground font-medium"
                      : "text-muted-foreground/70"
                }`}
                data-testid={`deep-analysis-stage-${s.key}`}
              >
                {isDone ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                ) : isActive ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                ) : (
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                {s.label}
              </li>
            );
          })}
        </ul>
        <p className="text-[11px] text-muted-foreground italic">
          Hang tight — estimated 2 minutes. This runs in the background, so you can navigate
          away and come back. The result is cached for 30 days.
        </p>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main wrapper / state machine (polling pattern)
// ---------------------------------------------------------------------------
const POLL_INTERVAL_MS = 5000;

const DeepAnalysisSection = ({ symbol, sector }) => {
  const { isPremium, isAdmin, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [openSpecs, setOpenSpecs] = useState([]);
  const fetchedSymbolRef = useRef(null);
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Reset everything when symbol changes
  useEffect(() => {
    stopPolling();
    setStatus(null);
    setAnalysis(null);
    setPersonal(null);
    setStatusLoading(true);
    fetchedSymbolRef.current = null;
  }, [symbol, stopPolling]);

  // ---- 1) status fetch (used both for initial load and polling tick) ----
  // Hardened: NEVER throws — all errors are swallowed and converted to a
  // safe fallback `status` so a single 4xx/5xx from this endpoint cannot
  // bubble up and break the Deep Analysis subtree.
  const fetchStatus = useCallback(async () => {
    if (!symbol) return null;
    try {
      const s = await apiGet(`/stocks/${encodeURIComponent(symbol)}/deep-analysis/status`);
      setStatus(s);
      return s;
    } catch (e) {
      // 401/403/5xx → fall back to locked state. This is intentionally
      // silent: we don't toast on every poll error since they're transient
      // and would spam the user.
      const fallback = { has_shared_analysis: false, is_premium: !!isPremium };
      setStatus(fallback);
      return fallback;
    }
  }, [symbol, isPremium]);

  // initial load
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    (async () => {
      await fetchStatus();
      if (alive) setStatusLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [symbol, fetchStatus]);

  // ---- 2) full payload (only when an analysis exists and the user can view) ----
  const loadFull = useCallback(async () => {
    if (!symbol) return;
    if (fetchedSymbolRef.current === symbol) return;
    try {
      const r = await apiGet(`/stocks/${encodeURIComponent(symbol)}/deep-analysis`);
      fetchedSymbolRef.current = symbol;
      setAnalysis(r.shared || null);
      setPersonal(r.personal || null);
    } catch {
      /* ignore */
    }
  }, [symbol]);

  useEffect(() => {
    if (status?.has_shared_analysis && (status?.can_view || isPremium || isAdmin)) {
      loadFull();
    }
  }, [status, loadFull, isPremium, isAdmin]);

  // ---- 3) polling loop while a job is running ----
  // Whenever the status indicates `is_running=true`, start a 5-second poll.
  // On terminal (`complete` / `failed`) → stop polling, refresh full payload.
  //
  // Hardening (post-incident): The polling tick is wrapped in a try/catch
  // that NEVER rethrows. If the network fails, we just skip this tick and
  // try again 5s later — we do NOT clear other page state and we do NOT
  // bubble the error up to the parent. This isolates the long-running
  // polling loop from the rest of the Stock Detail page.
  useEffect(() => {
    if (!status?.is_running) {
      stopPolling();
      return;
    }
    // Already polling? Don't double up.
    if (pollTimerRef.current) return;

    pollTimerRef.current = setInterval(async () => {
      try {
        const s = await fetchStatus();
        if (!s?.is_running) {
          stopPolling();
          if (s?.job_status === "complete") {
            // Refetch the full analysis (shared + personal)
            fetchedSymbolRef.current = null;
            try {
              await loadFull();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error("Deep Analysis: loadFull failed after complete", err);
            }
            toast.success("Deep analysis ready");
          } else if (s?.job_status === "failed") {
            const detail = s?.job_error || s?.progress?.stage_label || "Analysis failed";
            // Surface specific user-friendly toasts for known failure modes.
            const detailStr = String(detail);
            if (/budget exhausted|Budget has been exceeded|Max budget/i.test(detailStr)) {
              toast.error(
                "LLM budget exhausted. Admin must top up the Emergent universal key (Profile → Universal Key → Add Balance) before fresh analyses can run. Cached analyses remain available.",
                { duration: 12000 }
              );
            } else if (/timed out|TimeoutError/i.test(detailStr)) {
              toast.error(
                "Analysis timed out (>5 min). The LLM gateway may be slow — please try again shortly.",
                { duration: 8000 }
              );
            } else {
              toast.error(`Deep Analysis failed: ${detailStr.slice(0, 200)}`);
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Deep Analysis poll tick error (continuing):", err);
        // Don't stopPolling — transient errors should auto-recover.
      }
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [status?.is_running, fetchStatus, loadFull, stopPolling]);

  // CRITICAL: Cleanup on unmount — guarantees no orphan polling intervals
  // remain after the user navigates away from the Stock Detail page.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ---- 4) generate handler — kicks off backend job, then lets the poller take over ----
  // `opts.force=true` bypasses the cached-analysis reuse path on the
  // backend so the "Re-run analysis" button always produces a fresh run.
  const handleGenerate = useCallback(async (opts = {}) => {
    if (status?.is_running) return;
    const force = !!opts.force;
    try {
      const r = await apiPost(
        `/stocks/${encodeURIComponent(symbol)}/deep-analysis`,
        force ? { force: true } : undefined,
      );

      if (r?.status === "reused" && r?.analysis) {
        // Cached path — paint the result immediately. (Should never happen
        // on a force=true call, but guard anyway for safety.)
        setAnalysis(r.analysis);
        fetchedSymbolRef.current = symbol;
        await fetchStatus();
        try {
          const r2 = await apiGet(`/stocks/${encodeURIComponent(symbol)}/deep-analysis`);
          setPersonal(r2.personal || null);
        } catch {
          /* noop */
        }
        toast.success("Loaded existing deep analysis");
        return;
      }

      // Fresh job (or already-running) — eagerly flip status into running mode
      // so the UI re-renders the progress card without waiting on the next
      // /status round-trip. The polling effect then takes over for live updates.
      //
      // CRITICAL: we ALWAYS reset progress to a clean 0-elapsed initialising
      // state (do NOT inherit `prev.progress`), otherwise the UI would briefly
      // show the previous (completed) job's elapsed=N seconds before the next
      // poll tick overwrites it. That looked like "Running for 156s · Analysis
      // complete!" — confusing the user.
      const optimisticStartedAt = new Date().toISOString();
      setStatus((prev) => ({
        ...(prev || {}),
        is_running: true,
        job_status: "running",
        job_id: r?.job_id || null,
        // Anchor a started_at NOW so the client-side timer can start
        // counting up immediately (the backend will overwrite this on
        // the first poll tick with the authoritative server timestamp).
        started_at: r?.started_at || optimisticStartedAt,
        progress: r?.progress || {
          stage: "initialising",
          stage_label: "Loading stock fundamentals & macro/geo context…",
          completed_stages: 0,
          total_stages: 3,
          elapsed_seconds: 0,
        },
        // Clear any previous job_error so the new run isn't tainted by it.
        job_error: null,
      }));

      toast.info(
        r?.status === "running"
          ? "Analysis already in progress — tracking progress"
          : force
            ? "Fresh deep analysis started — this takes about 2 minutes"
            : "Deep analysis started — this takes about 2 minutes"
      );

      // Reconcile with backend on the next tick (fills in any missing fields).
      void fetchStatus();
    } catch (e) {
      // CRITICAL: Catch ALL errors locally. Never let a Deep Analysis
      // failure propagate to the parent (it must NOT break valuations
      // or other cards on the Stock Detail page).
      const detail = e?.response?.data?.detail || e?.message || "Failed to start";
      const status = e?.response?.status;
      if (status === 429) toast.error(`Quota exhausted: ${detail}`);
      else if (status === 403)
        toast.error("Deep Analysis is available on Premium plans");
      else if (status === 503)
        toast.warning(detail, { duration: 8000 });
      else if (/budget exhausted|Budget has been exceeded|Max budget/i.test(String(detail)))
        toast.error(
          "LLM budget exhausted. Admin must top up the Emergent universal key before fresh analyses can run.",
          { duration: 12000 }
        );
      else toast.error(`Deep Analysis failed: ${detail}`);
    }
  }, [status?.is_running, symbol, fetchStatus]);

  // Convenience handler for the "Re-run analysis" button (force=true).
  const handleRerun = useCallback(() => handleGenerate({ force: true }), [handleGenerate]);

  // ---- 5) personal context (free, ~5s) ----
  const handlePersonal = useCallback(async () => {
    setPersonalLoading(true);
    try {
      const r = await apiPost(`/stocks/${encodeURIComponent(symbol)}/deep-analysis/personal-context`);
      setPersonal(r?.personal || null);
      toast.success("Personal context added");
    } catch (e) {
      toast.error("Failed to add personal context");
    } finally {
      setPersonalLoading(false);
    }
  }, [symbol]);

  // ---- 6) State decision ----
  const renderState = useMemo(() => {
    if (statusLoading) return "loading";
    // A backend-tracked job is running for this symbol
    if (status?.is_running) return "running";
    // Locked: free / basic
    if (!isPremium && !isAdmin) return "locked";
    if (!status?.has_shared_analysis) return "empty";
    if (status?.is_outdated) return "outdated";
    return "available";
  }, [statusLoading, isPremium, isAdmin, status]);

  if (!symbol) return null;

  // Loading skeleton
  if (renderState === "loading") {
    return (
      <Card className="border-2" data-testid="deep-analysis-loading">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Deep Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (renderState === "running")
    return <RunningState symbol={symbol} progress={status?.progress} startedAt={status?.started_at} />;
  if (renderState === "locked") return <LockedState symbol={symbol} status={status} />;
  if (renderState === "empty")
    return (
      <EmptyState
        symbol={symbol}
        status={status}
        onGenerate={handleGenerate}
        isAdmin={isAdmin}
      />
    );

  // available OR outdated — render full analysis
  return (
    <Card
      className={`border-2 bg-card ${
        renderState === "outdated" ? "border-amber-500/40" : "border-primary/30"
      }`}
      data-testid="deep-analysis-section"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Deep Analysis · {symbol}
              {renderState === "outdated" && (
                <Badge
                  variant="outline"
                  className="border-amber-500/50 text-amber-700 dark:text-amber-400 text-[10px]"
                  data-testid="deep-analysis-outdated-badge"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Outdated · re-run recommended
                </Badge>
              )}
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
              <Clock className="h-3 w-3" />
              Generated {fmtAge(status?.analysis_age_days)}
              {analysis?.generated_at && (
                <span>· {formatDateTime(analysis.generated_at)}</span>
              )}
              {status?.valid_until && renderState !== "outdated" && (
                <span>· valid until {formatDateTime(status.valid_until).split(",")[0]}</span>
              )}
              {analysis?.model && <span className="font-mono">· {analysis.model}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Badge
                variant="outline"
                className="text-[11px] border-[hsl(var(--success))]/40 text-[hsl(var(--success))]"
                data-testid="deep-analysis-admin-badge"
              >
                <Shield className="h-3 w-3 mr-1" />
                Admin · Unlimited access
              </Badge>
            )}
            {/* BETA MODE — only admins can run / re-run analyses. Regular
                users see view-only output. */}
            {isAdmin && (
              <Button
                size="sm"
                variant={renderState === "outdated" ? "default" : "outline"}
                onClick={handleRerun}
                data-testid="deep-analysis-refresh-btn"
              >
                <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                {renderState === "outdated" ? "Re-run analysis" : "Re-run"}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {renderState === "outdated" && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex items-start gap-2 text-amber-700 dark:text-amber-300"
            data-testid="deep-analysis-outdated-banner"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">
                This analysis is{" "}
                <span className="tabular-nums">{status?.analysis_age_days ?? "—"} days old</span>.
              </div>
              <div className="text-xs leading-relaxed text-amber-700/90 dark:text-amber-300/90 mt-0.5">
                Market and macro conditions may have changed since this report was generated.
                {isAdmin
                  ? " Re-run to publish a fresh shared analysis for the community."
                  : " A fresh analysis will appear here once an administrator re-runs it."}
              </div>
            </div>
            {isAdmin && (
              <Button
                size="sm"
                onClick={handleRerun}
                data-testid="deep-analysis-rerun-banner-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Run fresh analysis
              </Button>
            )}
          </div>
        )}

        {!analysis && (
          <div className="rounded-lg border bg-muted/30 p-3.5 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading analysis…
          </div>
        )}

        {analysis && (
          <>
            <CoordinatorCard coord={analysis.coordinator} />

            {/* 6 specialists */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  6 Specialist views — click to expand
                </span>
                <div className="flex items-center gap-2 text-[11px]">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() =>
                      setOpenSpecs([
                        "value_analysis",
                        "quality_analysis",
                        "dividend_analysis",
                        "risk_analysis",
                        "geo_analysis",
                        "macro_analysis",
                      ])
                    }
                    data-testid="deep-analysis-expand-all"
                  >
                    Expand all
                  </button>
                  <span className="text-muted-foreground/50">·</span>
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setOpenSpecs([])}
                    data-testid="deep-analysis-collapse-all"
                  >
                    Collapse all
                  </button>
                </div>
              </div>
              <Accordion
                type="multiple"
                value={openSpecs}
                onValueChange={setOpenSpecs}
                className="grid grid-cols-1 lg:grid-cols-2 gap-2.5"
              >
                <SpecialistCard kind="value_analysis" data={analysis.value_analysis} />
                <SpecialistCard kind="quality_analysis" data={analysis.quality_analysis} />
                <SpecialistCard kind="dividend_analysis" data={analysis.dividend_analysis} />
                <SpecialistCard kind="risk_analysis" data={analysis.risk_analysis} />
                <SpecialistCard kind="geo_analysis" data={analysis.geo_analysis} />
                <SpecialistCard kind="macro_analysis" data={analysis.macro_analysis} />
              </Accordion>
            </div>

            {/* Personal context */}
            <PersonalContextCard
              personal={personal}
              loading={personalLoading}
              onGenerate={handlePersonal}
            />

            {/* Footer disclosure */}
            <div className="rounded-md border border-border/70 bg-muted/40 p-2.5 text-[11px] leading-snug text-muted-foreground flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                AI-generated research — reference only, not a buy/sell recommendation. All yields
                shown net of 10% withholding tax; returns include 2.42% round-trip brokerage.
                Macro &amp; geopolitical context refreshed weekly. View count:{" "}
                <span className="tabular-nums">{analysis.view_count ?? 1}</span>.
              </span>
            </div>
            <AiAnalysisWarning testId="cse-deep-analysis-warning" />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DeepAnalysisSection;
